<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { format } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';

import BillingCard from './components/BillingCard.vue';
import DetailItem from './components/DetailItem.vue';
import PlanSelector from './components/PlanSelector.vue';
import PaymentMethodForm from './components/PaymentMethodForm.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const { currentAccount } = useAccount();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

// State for billing modals
const showPaymentMethodForm = ref(false);
const billingError = ref('');

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  if (hasABillingPlan.value) return;

  await store.dispatch('accounts/subscription');
  await store.dispatch('accounts/get');
};

const handleBillingPageLogic = async () => {
  // Always fetch account details for subscriptions page
  await fetchAccountDetails();

  // Clear any existing refresh flag since we're showing the billing page
  sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
};

const onClickBillingPortal = async () => {
  billingError.value = '';

  // Check if user needs to add payment method
  if (!customAttributes.value.payment_method_added) {
    showPaymentMethodForm.value = true;
  } else {
    try {
      await store.dispatch('accounts/checkout');
    } catch (error) {
      billingError.value =
        error.response?.data?.error ||
        error.message ||
        'BILLING_SETTINGS.NO_BILLING_USER';
      useAlert(error.message || billingError.value);
    }
  }
};

const handlePaymentMethodSuccess = async () => {
  showPaymentMethodForm.value = false;
  // Refresh account details to update payment_method_added flag
  try {
    await store.dispatch('accounts/get');
    // Force re-render by updating the account in the store
    await new Promise(resolve => setTimeout(resolve, 500));
  } catch (error) {
    console.error('Failed to refresh account:', error);
  }
};

const closePaymentMethodForm = () => {
  showPaymentMethodForm.value = false;
};

onMounted(handleBillingPageLogic);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <div
          v-if="billingError"
          class="flex items-start gap-3 px-4 py-3 rounded-lg border border-n-weak bg-n-solid-2 text-sm text-n-slate-11"
          role="alert"
        >
          <i class="i-lucide-circle-alert text-n-amber-10 text-lg" />
          <span>
            {{
              billingError.startsWith('BILLING_SETTINGS.')
                ? $t(billingError)
                : billingError
            }}
          </span>
        </div>

        <!-- Payment Method Warning Banner -->
        <div
          v-if="hasABillingPlan && !customAttributes.payment_method_added"
          class="flex items-center justify-between px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <i class="i-lucide-alert-triangle text-yellow-600 text-xl" />
            <div>
              <p class="font-medium text-sm text-yellow-900">
                {{ $t('BILLING_SETTINGS.PAYMENT_BANNER.TITLE') }}
              </p>
              <p class="text-xs text-yellow-700">
                {{ $t('BILLING_SETTINGS.PAYMENT_BANNER.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <ButtonV4
            sm
            solid
            blue
            :is-loading="uiFlags.isCheckoutInProcess"
            :disabled="uiFlags.isCheckoutInProcess"
            @click="onClickBillingPortal"
          >
            {{ $t('BILLING_SETTINGS.PAYMENT_BANNER.BUTTON_TXT') }}
          </ButtonV4>
        </div>

        <!-- Available Plans Section -->
        <BillingCard
          :title="$t('BILLING_SETTINGS.PLANS.TITLE')"
          :description="$t('BILLING_SETTINGS.PLANS.DESCRIPTION')"
        >
          <PlanSelector
            class="px-5"
            @show-payment-form="showPaymentMethodForm = true"
          />
        </BillingCard>

        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              solid
              blue
              :is-loading="uiFlags.isCheckoutInProcess"
              :disabled="uiFlags.isCheckoutInProcess"
              @click="onClickBillingPortal"
            >
              {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </template>
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
          </div>
        </BillingCard>
      </section>
      <PaymentMethodForm
        v-if="showPaymentMethodForm"
        :account-id="currentAccount.id"
        @success="handlePaymentMethodSuccess"
        @close="closePaymentMethodForm"
      />
    </template>
  </SettingsLayout>
</template>
